import React, { useState, useContext, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { OperacaoContext } from "../../../../../context/OperacaoContext";
import useAuth from "../../../../../components/hooks/useAuth";
import PrivateMenuLeft from "../../../sections/Header/privateMenuLeft";
import PrivateMenuUp from "../../../sections/Header/privateMenuUp";
import PrivateMenuCampanha from "../privateMenuCampanha";

import './Operacoes.css';

const HEADER_HEIGHT = 56;

// Ícones SVG Inline para evitar dependências externas e erros de compilação
const IconUser = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>;
const IconCalendar = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>;
const IconClock = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>;
const IconCheck = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>;
const IconInfo = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>;
const IconSync = ({ className }) => <svg className={className} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></svg>;
const IconBan = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>;
const IconFilter = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>;

// Configuração de Status baseada no Enum TypeOfLinkedZapActOperationStatus
const STATUS_CONFIG = {
  PENDING: { id: 0, label: "Em Espera", color: "#ff9800" },
  SCHEDULED: { id: 1, label: "Agendado", color: "#9c27b0" },
  INPROGRESS: { id: 2, label: "Execução", color: "#3498db" },
  SUCESS: { id: 3, label: "Sucesso", color: "#2ecc40" },
  FAILED: { id: 4, label: "Falhou", color: "#e74c3c" },
  CANCELLED: { id: 5, label: "Cancelado", color: "#95a5a6" },
  SUPERSEDED: { id: 6, label: "Substituído", color: "#607d8b" }
};

// Definição das opções para o Select de Filtro
const STATUS_OPTIONS = [
  { id: "", name: "Todos os Status" },
  ...Object.values(STATUS_CONFIG).map(s => ({ id: s.id, name: s.label }))
];

const CampaignOperations = () => {
  const { id: campaignId } = useParams();
  const { sidebarOpen, postApi } = useAuth();
  const { operacoes, setOperacoes } = useContext(OperacaoContext);

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [menuAberto, setMenuAberto] = useState(null);

  // Estados dos Filtros
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusIdFilter, setStatusIdFilter] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    loadListOperations();
  }, [campaignId, activeTab]);

  const loadListOperations = async () => {
    setLoading(true);
    try {
      // Se estiver numa aba específica, o status_id da aba prevalece
      const currentStatusId = activeTab === "all" ? statusIdFilter : activeTab;

      const payload = {
        queryString: {
          operation: {
            ...(startDate && { start_date: startDate }),
            ...(endDate && { end_date: endDate }),
            ...(currentStatusId !== "" && { status_id: parseInt(currentStatusId) })
          },
          campaign: { id: campaignId }
        }
      };

      const response = await postApi("/api/v1/operations/linkedzap/list", payload);

      if (response && response.lista) {
        setOperacoes(response.lista);
      }
    } catch (error) {
      console.error("Erro ao carregar operações:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelarOperacao = async (opId) => {
    try {
      await postApi(`/api/v1/operations/linkedzap/cancel`, { operation_id: opId });
      setMenuAberto(null);
      loadListOperations();
    } catch (error) {
      console.error("Erro ao cancelar operação", error);
    }
  };

  const handleReprocessar = async (opId) => {
    try {
      await postApi(`/api/v1/operations/linkedzap/retry`, { operation_id: opId });
      setMenuAberto(null);
      loadListOperations();
    } catch (error) {
      console.error("Erro ao reprocessar", error);
    }
  };

  // Contadores para os Cards superiores baseados no estado atual
  const stats = useMemo(() => {
    const totals = { all: operacoes.length };
    Object.keys(STATUS_CONFIG).forEach(key => {
      totals[STATUS_CONFIG[key].id] = operacoes.filter(op => op.status?.name === key).length;
    });
    return totals;
  }, [operacoes]);

  // Fechar menu de ações ao clicar fora
  useEffect(() => {
    const handleClick = () => setMenuAberto(null);
    if (menuAberto !== null) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [menuAberto]);

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f8fafc" }}>
      <PrivateMenuLeft />
      <PrivateMenuUp />

      <div style={{
        marginLeft: sidebarOpen ? 220 : 70,
        width: "100%",
        transition: "margin-left 0.3s ease",
        padding: "0 20px"
      }}>
        <PrivateMenuCampanha />

        <nav style={{
          position: "fixed", top: 0, left: 0, width: "100%", height: HEADER_HEIGHT,
          background: "#005c53", zIndex: 1200, display: "flex", alignItems: "center",
          justifyContent: "space-between", paddingRight: 24
        }}>
          <div style={{ marginLeft: sidebarOpen ? 230 : 80, transition: "margin-left 0.3s" }}>
            <span style={{ color: "#fff", fontWeight: "bold", fontSize: 18 }}>Gestão de Operações</span>
          </div>


        </nav>

        <div style={{ paddingTop: HEADER_HEIGHT + 24, display: "flex", flexDirection: "column", alignItems: "center" }}>

          {/* Cards de Status Superiores */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
            gap: "12px",
            width: "100%",
            maxWidth: "1000px",
            marginBottom: "24px"
          }}>

            <div
              className={`summary-card ${activeTab === "all" ? "selected" : ""}`}
              onClick={() => setActiveTab("all")}
              style={{ borderLeft: "4px solid #005c53" }}
            >
              <div className="card-label">Geral</div>
            </div>
            {Object.values(STATUS_CONFIG).map((status) => (
              <div
                key={status.id}
                className={`summary-card ${activeTab === status.id.toString() ? "selected" : ""}`}
                onClick={() => setActiveTab(status.id.toString())}
                style={{ borderLeft: `4px solid ${status.color}` }}
              >
                <div className="card-label">{status.label}</div>
              </div>
            ))}
          </div>

          {/* Botão Nova Operação */}
          <div style={{ width: "100%", maxWidth: "1000px", display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
            <button
              onClick={() => navigate("add")}
              style={{
                background: "#005c53",
                color: "#fff",
                border: "none",
                padding: "12px 28px",
                borderRadius: "8px",
                fontWeight: "bold",
                fontSize: "15px",
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(0, 92, 83, 0.3)",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                transition: "all 0.2s"
              }}
              onMouseOver={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 16px rgba(0, 92, 83, 0.4)"; }}
              onMouseOut={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 92, 83, 0.3)"; }}
            >
              <span style={{ fontSize: "20px", lineHeight: 1 }}>+</span> Nova Operação
            </button>
          </div>


          {/* Painel de Filtros de Data e Status */}
          <div style={{
            background: "#fff",
            padding: "16px 24px",
            borderRadius: "12px",
            marginBottom: "24px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            display: "flex",
            gap: "16px",
            flexWrap: "wrap",
            alignItems: "flex-end",
            width: "100%",
            maxWidth: "1000px"
          }}>
            <div style={{ flex: 1, minWidth: "150px" }}>
              <label className="input-label">Data Início</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="custom-input" />
            </div>
            <div style={{ flex: 1, minWidth: "150px" }}>
              <label className="input-label">Data Fim</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="custom-input" />
            </div>
            {activeTab === "all" && (
              <div style={{ flex: 1, minWidth: "150px" }}>
                <label className="input-label">Status</label>
                <select value={statusIdFilter} onChange={e => setStatusIdFilter(e.target.value)} className="custom-input">
                  {STATUS_OPTIONS.map(opt => <option key={opt.id} value={opt.id}>{opt.name}</option>)}
                </select>
              </div>
            )}
            <button onClick={loadListOperations} className="btn-apply-filters">
              <IconFilter /> Filtrar
            </button>
          </div>

          {/* Listagem de Operações em Cards */}
          <div style={{ width: "100%", maxWidth: "1000px", paddingBottom: "40px" }}>
            {loading ? (
              <div style={{ textAlign: "center", padding: 60 }}><IconSync className="spin" /></div>
            ) : operacoes.length > 0 ? (
              operacoes.map((op, idx) => {
                const config = STATUS_CONFIG[op.status?.name] || { color: "#005c53" };
                return (
                  <div key={op.id} className="operation-item-card">
                    {/* Header Dinâmico baseado no Status */}
                    <div className="op-header" style={{ background: config.color }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <IconInfo />
                        <span style={{ fontWeight: "bold" }}>{op.operation?.label}</span>
                        <span className="op-id-badge">ID: {op.id.substring(0, 8)}</span>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                        <span style={{ fontSize: "12px" }}>Tentativas: {op.attempts} / {op.max_attempts}</span>
                        <div
                          style={{ cursor: "pointer", fontSize: "20px" }}
                          onClick={(e) => { e.stopPropagation(); setMenuAberto(menuAberto === idx ? null : idx); }}
                        >
                          &#8942;
                        </div>
                      </div>
                    </div>

                    {/* Menu de Contexto (Cancelar/Reprocessar) */}
                    {menuAberto === idx && (
                      <div className="op-actions-dropdown">
                        <div className="action-row" onClick={() => handleReprocessar(op.id)}>
                          <IconSync /> Reprocessar
                        </div>
                        <div className="action-row danger" onClick={() => handleCancelarOperacao(op.id)}>
                          <IconBan /> Cancelar Operação
                        </div>
                      </div>
                    )}

                    {/* Conteúdo do Card */}
                    <div className="op-body">
                      <div className="op-col status-col">
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", color: config.color, marginBottom: "8px" }}>
                          <IconCheck />
                          <strong style={{ fontSize: "14px" }}>{op.status?.name}</strong>
                        </div>
                        <div style={{ fontSize: "13px", color: "#64748b" }}>
                          <strong>Ação:</strong> {op.operation?.label}
                        </div>
                      </div>

                      <div className="op-col info-col">
                        <div className="op-info-row">
                          <IconUser />
                          <span><strong>Conta WhatsApp:</strong> {op.whatsapp_act?.name || "Desconhecida"}</span>
                        </div>
                        <div className="op-info-row">
                          <IconCalendar />
                          <span><strong>Agendamento:</strong> {new Date(op.scheduled_at).toLocaleString('pt-BR')}</span>
                        </div>
                        {op.retry_after && (
                          <div className="op-info-row retry-text">
                            <IconClock />
                            <span><strong>Próxima Tentativa:</strong> {new Date(op.retry_after).toLocaleString('pt-BR')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="no-data-panel">
                <IconInfo />
                <p>Nenhuma operação encontrada para os critérios selecionados.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .summary-card {
          background: #fff; padding: 16px; border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1); cursor: pointer;
          transition: all 0.2s ease; border: 1px solid transparent;
        }
        .summary-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
        .summary-card.selected { background: #f0fdf4; border-color: #005c53; }
        .summary-card .card-label { font-size: 11px; font-weight: bold; color: #64748b; text-transform: uppercase; margin-bottom: 4px; }
        .summary-card .card-value { font-size: 20px; font-weight: 800; color: #1e293b; }

        .input-label { display: block; font-size: 11px; font-weight: bold; color: #94a3b8; text-transform: uppercase; margin-bottom: 4px; }
        .custom-input { width: 100%; padding: 10px 14px; border-radius: 8px; border: 1px solid #e2e8f0; font-size: 13px; outline: none; transition: border-color 0.2s; }
        .custom-input:focus { border-color: #005c53; }
        .btn-apply-filters { background: #005c53; color: #fff; border: none; padding: 10px 20px; border-radius: 8px; font-weight: 600; font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 8px; }

        .operation-item-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; margin-bottom: 16px; overflow: hidden; position: relative; }
        .op-header { padding: 12px 20px; color: #fff; display: flex; justify-content: space-between; align-items: center; }
        .op-id-badge { font-size: 10px; background: rgba(0,0,0,0.2); padding: 2px 6px; border-radius: 4px; font-weight: normal; }
        
        .op-actions-dropdown { position: absolute; right: 20px; top: 48px; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 10; min-width: 190px; }
        .action-row { padding: 12px 16px; display: flex; align-items: center; gap: 10px; font-size: 13px; cursor: pointer; color: #334155; transition: background 0.2s; }
        .action-row:hover { background: #f8fafc; }
        .action-row.danger { color: #e11d48; border-top: 1px solid #f1f5f9; }

        .op-body { display: flex; padding: 20px; gap: 24px; flex-wrap: wrap; }
        .op-col.status-col { flex: 1; min-width: 180px; border-right: 1px solid #f1f5f9; }
        .op-col.info-col { flex: 1.5; min-width: 250px; display: flex; flex-direction: column; gap: 10px; }
        .op-info-row { display: flex; align-items: center; gap: 10px; font-size: 13px; color: #475569; }
        .op-info-row.retry-text { color: #e11d48; }

        .no-data-panel { text-align: center; padding: 80px; color: #94a3b8; background: #fff; border-radius: 12px; width: 100%; border: 1px dashed #e2e8f0; }
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        @media (max-width: 768px) {
          .op-col.status-col { border-right: none; border-bottom: 1px solid #f1f5f9; padding-bottom: 16px; }
        }
      `}</style>
    </div>
  );
};

export default CampaignOperations;